/**
 * WebAuthn helpers — steer the browser's authenticator picker (OPP-132/133).
 *
 * The browser draws the passkey / "use a phone" UI itself; we can only shape
 * the options it receives:
 *   - hints (WebAuthn L3): 'client-device' = this computer, 'hybrid' = phone via QR
 *   - authenticatorAttachment (registration): platform vs cross-platform
 *   - allowCredentials transports: Kratos stores none, so we set them per mode —
 *     without them Windows assumes a USB security key.
 */

export type AuthenticatorMode = 'any' | 'device' | 'phone';

/** sessionStorage key: authenticator code handed over by a QR login approval (OPP-144). */
export const PENDING_TOTP_KEY = 'passport_pending_totp';
/** Kratos accepts a TOTP code for roughly one step either side; don't try older handoffs. */
export const PENDING_TOTP_MAX_AGE_MS = 60_000;

export const b64urlDecode = (v: string): Uint8Array =>
  Uint8Array.from(atob(v.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

export const b64urlEncode = (v: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(v)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

type JsonObject = Record<string, unknown>;

// Kratos v1.1 doesn't store credential transports, so allowCredentials arrives
// without them and Windows falls back to "insert your security key". Tell the
// browser where to look instead.
const TRANSPORTS: Record<AuthenticatorMode, string[]> = {
  device: ['internal'],
  phone: ['hybrid'],
  any: ['internal', 'hybrid', 'usb', 'nfc', 'ble'],
};

/** Adjust assertion (login / approval) options in place. */
export function steerAssertion(publicKey: JsonObject, mode: AuthenticatorMode): void {
  const allow = publicKey.allowCredentials as JsonObject[] | undefined;
  if (allow) {
    publicKey.allowCredentials = allow.map((c) => ({ ...c, transports: TRANSPORTS[mode] }));
  }
  if (mode === 'phone') publicKey.hints = ['hybrid'];
  else if (mode === 'device') publicKey.hints = ['client-device'];
  else delete publicKey.hints;
}

/** Adjust registration options in place — where the new passkey is saved. */
export function steerRegistration(publicKey: JsonObject, mode: AuthenticatorMode): void {
  const selection = { ...((publicKey.authenticatorSelection as JsonObject) || {}) };
  if (mode === 'phone') {
    selection.authenticatorAttachment = 'cross-platform';
    // Kratos (passwordless: false) sends residentKey "discouraged"; browsers only
    // offer the phone/QR path for discoverable credentials, which phones always make.
    selection.residentKey = 'required';
    selection.requireResidentKey = true;
    publicKey.hints = ['hybrid'];
  } else if (mode === 'device') {
    selection.authenticatorAttachment = 'platform';
    publicKey.hints = ['client-device'];
  } else {
    delete selection.authenticatorAttachment;
    delete publicKey.hints;
  }
  publicKey.authenticatorSelection = selection;
}

/**
 * Whether this browser + OS can use a passkey on a phone (hybrid / QR).
 * Firefox and Edge on Windows hand passkeys to the Windows dialog, which only
 * offers phones on Windows 11 with Bluetooth. null = the browser doesn't say.
 */
export async function canUsePhonePasskey(): Promise<boolean | null> {
  try {
    const pkc = window.PublicKeyCredential as unknown as
      | { getClientCapabilities?: () => Promise<Record<string, boolean>> }
      | undefined;
    if (!pkc?.getClientCapabilities) return null;
    const caps = await pkc.getClientCapabilities();
    return typeof caps.hybridTransport === 'boolean' ? caps.hybridTransport : null;
  } catch {
    return null;
  }
}

export const PHONE_PASSKEY_UNAVAILABLE =
  "This browser can't reach a passkey on your phone from this computer. That needs Windows 11 " +
  'with Bluetooth on, or Chrome. Use another option.';

/** Run navigator.credentials.get from base64url JSON options; returns JSON for the server. */
export async function getAssertion(publicKey: JsonObject, mode: AuthenticatorMode): Promise<JsonObject> {
  const opts = { ...publicKey };
  steerAssertion(opts, mode);
  opts.challenge = b64urlDecode(opts.challenge as string);
  opts.allowCredentials = ((opts.allowCredentials as JsonObject[]) || []).map((c) => ({
    ...c,
    id: b64urlDecode(c.id as string),
  }));

  const credential = (await navigator.credentials.get({
    publicKey: opts as unknown as PublicKeyCredentialRequestOptions,
  })) as PublicKeyCredential;
  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: b64urlEncode(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: b64urlEncode(response.authenticatorData),
      clientDataJSON: b64urlEncode(response.clientDataJSON),
      signature: b64urlEncode(response.signature),
      userHandle: response.userHandle ? b64urlEncode(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}
